"use client"

import * as React from "react"
import { Check, ChevronDown, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { countryCodes } from "@/data/countryCodes"

interface CountryCodePickerProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
}

export function CountryCodePicker({ value, onChange, className }: CountryCodePickerProps) {
    const [open, setOpen] = React.useState(false)
    const [query, setQuery] = React.useState("")
    const containerRef = React.useRef<HTMLDivElement>(null)
    const inputRef = React.useRef<HTMLInputElement>(null)

    const selectedCountry = countryCodes.find((country) => country.code === value) || countryCodes.find(c => c.code === 'US');

    const filteredCountries = query === ""
        ? countryCodes
        : countryCodes.filter((country) =>
            country.name.toLowerCase().includes(query.toLowerCase()) ||
            country.dial_code.includes(query) ||
            country.code.toLowerCase().includes(query.toLowerCase())
        )

    // Close on outside click
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false)
                setQuery("")
            }
        }
        if (open) {
            document.addEventListener("mousedown", handleClickOutside)
            return () => document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [open])

    // Focus input when dropdown opens
    React.useEffect(() => {
        if (open) {
            // Small delay to ensure the dropdown is rendered
            requestAnimationFrame(() => {
                inputRef.current?.focus()
            })
        }
    }, [open])

    const handleSelect = (code: string) => {
        onChange(code)
        setOpen(false)
        setQuery("")
    }

    return (
        <div ref={containerRef} className={cn("relative", className)}>
            {/* Trigger button */}
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex h-[52px] w-full items-center justify-between rounded-xl border-2 border-site-border bg-site-bg px-2 py-2 text-sm transition-colors focus:outline-none accent-focus"
            >
                <div className="flex items-center gap-1">
                    <span className="text-base">{selectedCountry?.flag}</span>
                    <span className="text-site-text font-medium text-md">{selectedCountry?.dial_code}</span>
                </div>
                <ChevronDown className="h-3 w-3 text-site-muted shrink-0" />
            </button>

            {/* Dropdown */}
            {open && (
                <div className="absolute top-full left-0 z-50 mt-1 w-[300px] rounded-xl border border-site-border bg-site-bg shadow-lg overflow-hidden">
                    {/* Search input */}
                    <div className="flex items-center border-b border-site-border px-3">
                        <Search className="mr-2 h-4 w-4 shrink-0 text-site-muted" />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search country..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-site-muted"
                        />
                    </div>

                    {/* Country list */}
                    <div className="max-h-[300px] overflow-y-auto p-1">
                        {filteredCountries.length === 0 ? (
                            <div className="py-6 text-center text-sm text-site-muted">No country found.</div>
                        ) : (
                            filteredCountries.map((country) => (
                                <button
                                    key={country.code}
                                    type="button"
                                    onClick={() => handleSelect(country.code)}
                                    className="flex w-full items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-site-text/5 text-left"
                                >
                                    <span className="text-xl">{country.flag}</span>
                                    <span className="flex-1 truncate text-sm">{country.name}</span>
                                    <span className="text-site-muted text-sm">{country.dial_code}</span>
                                    {value === country.code && (
                                        <Check className="h-4 w-4 text-site-success" />
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
